import { inject, Injectable } from '@angular/core';
import { ContactInfoData, LocationEntry, migrateSurveyLocationPriorities, Survey, SurveyPhoto } from '../models/survey.model';
import { PHOTO_ANNOTATION_FLATTENER } from './photo-annotation-flattener.token';
import { AuthDtoSignerService } from '@damofire/data-security';
import { IdbSurveyService } from './idb-survey.service';
import { countSurvey, SurveyLogContext, SurveyLogService } from './survey-log.service';
import { SurveySnapshotService } from './survey-snapshot.service';
import { LoadedSurveyDoc, OnlineState, SurveyStorageService } from './survey-storage.service';
import { doc, DocumentReference, DocumentSnapshot, Firestore, getDoc, getDocFromServer, Timestamp } from '@angular/fire/firestore';
import { COMMONS_ENV } from '@damofire/data-angular-commons';
import { getBlob, getDownloadURL, ref, Storage } from '@angular/fire/storage';
import pLimit from 'p-limit';
import { captureMessage } from '@sentry/angular';
import {
  Audit,
  blobToBase64,
  convertDateToTimestamp,
  convertTimestampToDateString,
  convertTimestampToMillis,
  HttpUtils,
  logger,
  NEXUS_SURVEYS_COLLECTION,
  writeFSFile,
} from '@damofire/data-commons';

const UPLOAD_PHOTO_CONCURRENCY = 6;
const DELETE_PHOTO_CONCURRENCY = 10;
const PHOTO_UPLOAD_MAX_ATTEMPTS = 3;
const PHOTO_UPLOAD_RETRY_BASE_MS = 500;

export class SurveyNotConfirmedOnlineError extends Error {
  constructor(surveyId: string) {
    super(`Survey ${surveyId} is not confirmed online yet.`);
    this.name = 'SurveyNotConfirmedOnlineError';
  }
}

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

type SurveyDate = Timestamp | string | null;

interface FirestoreContactInfo extends Omit<ContactInfoData, 'dateOfSurvey'> {
  dateOfSurvey: SurveyDate;
}

interface FirestoreSurvey extends Omit<Survey, 'id' | 'updatedAt' | 'lastPublishedDate' | 'contactInfo'> {
  _id: string;
  updatedAt: Timestamp;
  lastPublishedDate: Timestamp;
  contactInfo: FirestoreContactInfo | null;
}

interface PhotoContext {
  measurementIndex: number;
  rowIndex: number;
  photoIndex: number;
  location: Partial<LocationEntry>;
  photo: SurveyPhoto;
}

interface InstallerPhotoContext {
  photoIndex: number;
  photos: SurveyPhoto[];
  photo: SurveyPhoto;
}

type PhotoUploadOutcome = 'uploaded' | 'failed' | 'status-updated' | 'skipped';

@Injectable({
  providedIn: 'root',
})
export class FirebaseSurveyService {
  private readonly authDtoSigner = inject(AuthDtoSignerService);
  private readonly idbSurveyService = inject(IdbSurveyService);
  private readonly environment = inject(COMMONS_ENV);
  private readonly surveyStorage = inject(SurveyStorageService);
  private readonly firestore = inject(Firestore);
  private readonly storage = inject(Storage);
  private readonly surveyLog = inject(SurveyLogService);
  private readonly snapshots = inject(SurveySnapshotService);
  private readonly flattenAnnotations = inject(PHOTO_ANNOTATION_FLATTENER, { optional: true });
  private readonly downloadUrlCache = new Map<string, string>();

  async getSurveyWithFallback(surveyId: string): Promise<Survey | null> {
    try {
      const localSurvey = await this.idbSurveyService.getSurvey(surveyId);
      if (localSurvey) {
        return localSurvey;
      }
    } catch (e) {
      this.surveyLog.warn('survey.load', 'IDB unavailable, falling back to Firestore', { surveyId, source: 'idb', fallback: 'firestore', error: e });
      captureMessage(`IDB read failed for survey ${surveyId}, falling back to Firestore. [${e}]`, 'warning');
    }

    this.surveyLog.warn('survey.load', 'Survey not in IDB, fetching from Firestore', { surveyId, source: 'firestore' });
    return this.fetchSurvey(surveyId);
  }

  async fetchRemoteSurvey(surveyId: string): Promise<Survey | null> {
    return this.fetchSurveyFromFirestore(surveyId);
  }

  async getPhotoContent(photo: SurveyPhoto): Promise<string> {
    try {
      return await this.idbSurveyService.getPhotoContent(photo.filepath);
    } catch {
      return this.getRemotePhotoUrl(photo);
    }
  }

  async getPhotoContentForEditor(photo: SurveyPhoto): Promise<string> {
    try {
      return await this.idbSurveyService.getPhotoContent(photo.filepath);
    } catch {
      return this.getRemotePhotoUrl(photo, true);
    }
  }

  private async getRemotePhotoUrl(photo: SurveyPhoto, preferOriginal = false): Promise<string> {
    const storagePath = preferOriginal ? (photo.original ?? photo.web) : (photo.web ?? photo.original);
    if (!storagePath) {
      throw new Error(`Photo ${photo.filename} has no local file and no remote storage path`);
    }

    const cached = this.downloadUrlCache.get(storagePath);
    if (cached) {
      return cached;
    }

    const url = await this.getDownloadUrl(storagePath);
    this.downloadUrlCache.set(storagePath, url);
    return url;
  }

  async fetchSurvey(surveyId: string): Promise<Survey | null> {
    const survey = await this.fetchSurveyFromFirestore(surveyId);

    if (survey) {
      try {
        await this.idbSurveyService.updateSurvey(survey);
        this.surveyLog.success('survey.load', { surveyId, source: 'firestore', after: countSurvey(survey) });
      } catch (e) {
        this.surveyLog.failure('survey.load', e, { surveyId, source: 'idb', note: 'IDB write failed after Firestore fetch, survey loaded but not cached' });
      }
    }

    return survey;
  }

  async copyRemoteSurveyToIdb(surveyId: string): Promise<{ downloaded: number; failed: number }> {
    return this.surveyLog.track('survey.copy', { surveyId, source: 'firestore' }, () => this.downloadRemoteSurvey(surveyId));
  }

  private async downloadRemoteSurvey(surveyId: string): Promise<{ downloaded: number; failed: number }> {
    const survey = await this.fetchSurvey(surveyId);
    if (!survey) {
      throw new Error(`Survey ${surveyId} not found online`);
    }

    const photos = [...this.getAllPhotoContexts(survey).map((context) => context.photo), ...this.getInstallerPhotoContexts(survey).map((context) => context.photo)];
    const uniquePhotos = [...new Map(photos.filter((photo) => photo.filepath).map((photo) => [photo.filepath, photo])).values()];
    const limit = pLimit(UPLOAD_PHOTO_CONCURRENCY);
    const results = await Promise.allSettled(uniquePhotos.map((photo) => limit(() => this.copyRemotePhotoToIdb(photo))));

    return {
      downloaded: results.filter((result) => result.status === 'fulfilled').length,
      failed: results.filter((result) => result.status === 'rejected').length,
    };
  }

  private async copyRemotePhotoToIdb(photo: SurveyPhoto): Promise<void> {
    const storagePath = photo.original ?? photo.web ?? photo.thumb;
    if (!storagePath) {
      throw new Error(`Photo ${photo.filename} has no remote storage path`);
    }

    const content = await blobToBase64(await this.getRemoteBlob(storagePath));
    await this.writeLocalFile(photo.filepath, content);
  }

  private getDownloadUrl(storagePath: string): Promise<string> {
    return getDownloadURL(ref(this.storage, storagePath));
  }

  private getRemoteBlob(storagePath: string): Promise<Blob> {
    return getBlob(ref(this.storage, storagePath));
  }

  private writeLocalFile(filepath: string, content: string): Promise<void> {
    return writeFSFile(filepath, content);
  }

  async publishSurvey(survey: Survey): Promise<Survey> {
    return this.surveyLog.track('survey.publish', { surveyId: survey.id, source: 'firestore' }, async (tracked) => {
      tracked.before = countSurvey(survey);
      await this.snapshots.take(survey, 'publish');
      const published = await this.uploadSurvey(survey, tracked);
      tracked.after = countSurvey(published);
      return published;
    });
  }

  private warnIfPublishShrinksRemote(survey: Survey, previousSurvey: Survey | null): void {
    if (!previousSurvey) {
      return;
    }

    const local = countSurvey(survey);
    const remote = countSurvey(previousSurvey);
    if (local.locations >= remote.locations && local.sheets >= remote.sheets) {
      return;
    }

    this.surveyLog.warn('survey.publish', 'Publishing a survey with fewer sheets or locations than the online version', {
      surveyId: survey.id,
      source: 'firestore',
      before: remote,
      after: local,
    });
  }

  private async uploadSurvey(survey: Survey, tracked: SurveyLogContext): Promise<Survey> {
    const now = Date.now();
    const { syncState: _localOnlyState, ...publishableSurvey } = survey;
    const updatedSurvey: Survey = {
      ...publishableSurvey,
      lastPublishedDate: now,
    };

    const { survey: previousSurvey, state: onlineState } = await this.readRemote(survey.id);
    this.warnIfPublishShrinksRemote(survey, previousSurvey);
    tracked['remoteCounts'] = previousSurvey ? countSurvey(previousSurvey) : null;

    await this.uploadAllLocationPhotos(updatedSurvey);
    await this.uploadAllInstallerPhotos(updatedSurvey);

    const preservedTicketId = updatedSurvey.hubspotTicketId ?? previousSurvey?.hubspotTicketId;
    const surveyWithPreservedTicket: Survey = {
      ...updatedSurvey,
      ...(preservedTicketId !== undefined && { hubspotTicketId: preservedTicketId }),
    };

    const sanitizedSurvey = this.stripUndefined(surveyWithPreservedTicket);

    const surveyDataForFirestore: FirestoreSurvey = {
      ...sanitizedSurvey,
      _id: updatedSurvey.id,
      updatedAt: Timestamp.fromMillis(updatedSurvey.updatedAt),
      lastPublishedDate: Timestamp.fromMillis(now),
    };
    if (updatedSurvey.contactInfo) {
      surveyDataForFirestore.contactInfo = {
        ...updatedSurvey.contactInfo,
        dateOfSurvey: convertDateToTimestamp(updatedSurvey.contactInfo.dateOfSurvey),
      };
    }
    surveyDataForFirestore.audit = await this.buildPublishAudit(previousSurvey?.audit);

    tracked['layout'] = await this.surveyStorage.save(`${NEXUS_SURVEYS_COLLECTION}/${updatedSurvey.id}`, surveyDataForFirestore, onlineState);

    // Only now that the new data is saved is it safe to remove photos the previous version referenced.
    if (previousSurvey) {
      await this.deleteOrphanedPhotos(previousSurvey, updatedSurvey);
      await this.deleteOrphanedInstallerPhotos(previousSurvey, updatedSurvey);
    }

    await this.idbSurveyService.updateSurvey(surveyWithPreservedTicket, { preserveUpdatedAt: true });

    return surveyWithPreservedTicket;
  }

  private async buildPublishAudit(previousAudit: Survey['audit']): Promise<Audit> {
    if (previousAudit?.createdBy) {
      const { audit: stamp } = (await this.authDtoSigner.sign({}, false, false)) as { audit: Audit };
      return { ...previousAudit, lastModifiedBy: stamp.lastModifiedBy, lastModifiedDate: stamp.lastModifiedDate };
    }

    const { audit: created } = (await this.authDtoSigner.sign({}, true)) as { audit: Audit };
    return { ...created, lastModifiedDate: created.createdDate };
  }

  async uploadLocationPhoto(surveyId: string, measurementIndex: number, rowIndex: number, filename: string, bytes: string, mediaId?: string): Promise<SurveyPhoto> {
    return this.postPhotoUpload(`nexus-surveys/surveys/${surveyId}/measurements/${measurementIndex}/locations/${rowIndex}/photos`, filename, bytes, mediaId);
  }

  async uploadInstallerPhoto(surveyId: string, filename: string, bytes: string, mediaId?: string): Promise<SurveyPhoto> {
    return this.postPhotoUpload(`nexus-surveys/surveys/${surveyId}/installer-photos`, filename, bytes, mediaId);
  }

  private async postPhotoUpload(collectionEndpoint: string, filename: string, bytes: string, mediaId?: string): Promise<SurveyPhoto> {
    const image = bytes.replace(/data:image\/[a-zA-Z]+;base64,/, '');
    let lastError: unknown;
    for (let attempt = 1; attempt <= PHOTO_UPLOAD_MAX_ATTEMPTS; attempt++) {
      try {
        return await HttpUtils.post<SurveyPhoto>(this.environment.appConfig.fns, collectionEndpoint, { filename, image, _id: mediaId });
      } catch (error) {
        lastError = error;
        if (attempt < PHOTO_UPLOAD_MAX_ATTEMPTS) {
          const backoffMs = Math.pow(2, attempt - 1) * PHOTO_UPLOAD_RETRY_BASE_MS;
          logger.warn('Photo upload failed, retrying', { category: 'survey', attempt, filename, backoffMs, error });
          await delay(backoffMs);
        }
      }
    }

    logger.error('Photo upload failed after all attempts', { category: 'survey', filename, error: lastError });
    captureMessage(`Photo upload failed after ${PHOTO_UPLOAD_MAX_ATTEMPTS} attempts. [${filename}]`, {
      level: 'error',
      extra: { collectionEndpoint, filename, mediaId, err: lastError || 'no error object' },
    });
    throw lastError;
  }

  private deletePhotoAt(photoEndpoint: string, photo: SurveyPhoto): Promise<void> {
    if (!photo.imageFolderPath) {
      logger.warn('Photo missing imageFolderPath, cannot delete', { category: 'survey', filename: photo.filename });
      throw new Error(`Photo ${photo.filename} is missing imageFolderPath`);
    }
    return HttpUtils.delete(this.environment.appConfig.fns, photoEndpoint, { imageFolderPath: photo.imageFolderPath });
  }

  async exportToXlsx(surveyId: string): Promise<void> {
    const endpoint = `nexus-surveys/surveys/${surveyId}/export-xlsx`;
    await HttpUtils.get(this.environment.appConfig.fns, endpoint);
  }

  async exportLocationsToXlsx(surveyId: string): Promise<string> {
    const endpoint = `nexus-surveys/surveys/${surveyId}/export-locations-xlsx`;
    const response = await HttpUtils.get<{ url: string }>(this.environment.appConfig.fns, endpoint);
    return response.url;
  }

  async sendToHubspot(surveyId: string): Promise<void> {
    const endpoint = `nexus-surveys/surveys/${surveyId}/send-to-hubspot`;
    await this.assertConfirmedOnline(surveyId);
    await HttpUtils.post(this.environment.appConfig.fns, endpoint);
    await this.snapshotLocalCopyBeforeHubspotSend(surveyId);
    await this.syncHubspotTicketId(surveyId);
    await this.idbSurveyService.patchHubspotPublishedAt(surveyId, Date.now());
  }

  private async syncHubspotTicketId(surveyId: string): Promise<void> {
    let local: Survey | undefined;
    try {
      local = await this.idbSurveyService.getSurvey(surveyId);
    } catch (error: unknown) {
      this.surveyLog.failure('survey.load', error, { surveyId, source: 'idb' });
      return;
    }

    if (!local) {
      await this.fetchSurvey(surveyId);
      return;
    }

    const ticketId = await this.readRemoteHubspotTicketId(surveyId);
    if (!ticketId || local.hubspotTicketId === ticketId) {
      return;
    }

    try {
      await this.idbSurveyService.patchHubspotTicketId(surveyId, ticketId);
    } catch (error: unknown) {
      this.surveyLog.failure('survey.save', error, { surveyId, source: 'idb', note: 'HubSpot ticket id not patched locally' });
    }
  }

  private async readRemoteHubspotTicketId(surveyId: string): Promise<string | undefined> {
    try {
      const snapshot = await this.getDocSnapshotFromServer(this.getDocRef(`${NEXUS_SURVEYS_COLLECTION}/${surveyId}`));
      const data = snapshot.exists() ? (snapshot.data() as { hubspotTicketId?: string } | undefined) : undefined;
      return data?.hubspotTicketId;
    } catch (error: unknown) {
      this.surveyLog.warn('survey.load', 'Could not read the HubSpot ticket id after sending', { surveyId, source: 'firestore', error });
      return undefined;
    }
  }

  protected getDocRef(path: string): DocumentReference {
    return doc(this.firestore, path);
  }

  protected async getDocSnapshot(docRef: DocumentReference): Promise<DocumentSnapshot> {
    return getDoc(docRef);
  }

  protected async getDocSnapshotFromServer(docRef: DocumentReference): Promise<DocumentSnapshot> {
    return getDocFromServer(docRef);
  }

  private async assertConfirmedOnline(surveyId: string): Promise<void> {
    let snapshot: DocumentSnapshot | null = null;
    try {
      snapshot = await this.getDocSnapshot(this.getDocRef(`${NEXUS_SURVEYS_COLLECTION}/${surveyId}`));
    } catch (error: unknown) {
      this.surveyLog.warn('survey.load', 'Could not confirm survey online before HubSpot send', { surveyId, source: 'firestore', error });
    }

    if (!snapshot?.exists() || snapshot.metadata.hasPendingWrites || snapshot.metadata.fromCache) {
      throw new SurveyNotConfirmedOnlineError(surveyId);
    }
  }

  /** Keep a restore point before a HubSpot send so the local copy can always be recovered. */
  private async snapshotLocalCopyBeforeHubspotSend(surveyId: string): Promise<void> {
    try {
      const local = await this.idbSurveyService.getSurvey(surveyId);
      if (local) {
        await this.snapshots.take(local, 'before-hubspot-refresh');
      }
    } catch (error: unknown) {
      this.surveyLog.failure('survey.snapshot', error, { surveyId, source: 'idb', reason: 'before-hubspot-refresh' });
    }
  }

  async duplicateSurvey(surveyId: string, newSurveyName: string): Promise<string> {
    const endpoint = `nexus-surveys/surveys/${surveyId}/duplicate`;
    return this.surveyLog.track('survey.duplicate', { surveyId, source: 'firestore' }, async (tracked) => {
      const response = await HttpUtils.post<{ newSurveyId: string }>(this.environment.appConfig.fns, endpoint, { newSurveyName });
      tracked['newSurveyId'] = response.newSurveyId;
      await this.fetchSurvey(response.newSurveyId);
      return response.newSurveyId;
    });
  }

  async deleteSurvey(surveyId: string): Promise<void> {
    await this.surveyLog.track('survey.delete', { surveyId, source: 'firestore' }, async () => {
      await this.surveyStorage.deleteAll(`${NEXUS_SURVEYS_COLLECTION}/${surveyId}`);
    });
  }

  async deleteLocationPhoto(surveyId: string, measurementIndex: number, rowIndex: number, photo: SurveyPhoto): Promise<void> {
    const photoIdSafe = encodeURIComponent(photo.filepath.split('/').pop() || photo.filename);
    await this.deletePhotoAt(`nexus-surveys/surveys/${surveyId}/measurements/${measurementIndex}/locations/${rowIndex}/photos/${photoIdSafe}`, photo);
    logger.info('Location photo deleted', { category: 'survey', filepath: photo.filepath });
  }

  async deleteInstallerPhoto(surveyId: string, photo: SurveyPhoto): Promise<void> {
    const photoIdSafe = encodeURIComponent(photo.filepath.split('/').pop() || photo.filename);
    await this.deletePhotoAt(`nexus-surveys/surveys/${surveyId}/installer-photos/${photoIdSafe}`, photo);
    logger.info('Installer photo deleted', { category: 'survey', filepath: photo.filepath });
  }

  private async fetchSurveyFromFirestore(surveyId: string): Promise<Survey | null> {
    return (await this.readRemote(surveyId)).survey;
  }

  private async readRemote(surveyId: string): Promise<{ survey: Survey | null; state: OnlineState }> {
    try {
      const remote = await this.loadRemote(surveyId);
      return remote ? { survey: remote.survey, state: { kind: 'found', doc: remote.loaded } } : { survey: null, state: { kind: 'missing' } };
    } catch (error) {
      this.surveyLog.failure('survey.load', error, { surveyId, source: 'firestore' });
      return { survey: null, state: { kind: 'unreadable' } };
    }
  }

  private async loadRemote(surveyId: string): Promise<{ survey: Survey; loaded: LoadedSurveyDoc } | null> {
    const loaded = await this.surveyStorage.load(`${NEXUS_SURVEYS_COLLECTION}/${surveyId}`);
    if (!loaded) {
      return null;
    }

    const firestoreSurvey = loaded.data as unknown as FirestoreSurvey;

    const survey: Survey = {
      ...firestoreSurvey,
      id: firestoreSurvey._id || surveyId,
      contactInfo: firestoreSurvey.contactInfo
        ? {
            ...firestoreSurvey.contactInfo,
            dateOfSurvey: convertTimestampToDateString(firestoreSurvey.contactInfo.dateOfSurvey),
          }
        : null,
      updatedAt: convertTimestampToMillis(firestoreSurvey.updatedAt) ?? Date.now(),
      lastPublishedDate: convertTimestampToMillis(firestoreSurvey.lastPublishedDate),
    };
    // Remote surveys written before the rename still carry legacy priority
    // values; normalize in memory so old rows render instead of blank.
    migrateSurveyLocationPriorities(survey);
    return { survey, loaded };
  }

  private getAllPhotoContexts(survey: Survey): PhotoContext[] {
    const contexts: PhotoContext[] = [];
    for (const [measurementIndexStr, measurement] of Object.entries(survey.measurements)) {
      const measurementIndex = parseInt(measurementIndexStr, 10);
      const locations = measurement.locationData?.locations || [];

      for (let rowIndex = 0; rowIndex < locations.length; rowIndex++) {
        const location = locations[rowIndex];
        if (!location?.photos?.length) {
          continue;
        }

        for (let photoIndex = 0; photoIndex < location.photos.length; photoIndex++) {
          contexts.push({
            measurementIndex,
            rowIndex,
            photoIndex,
            location,
            photo: location.photos[photoIndex],
          });
        }
      }
    }
    return contexts;
  }

  private getInstallerPhotoContexts(survey: Survey): InstallerPhotoContext[] {
    const photos = survey.installerPhotos || [];
    return photos.map((photo, photoIndex) => ({ photoIndex, photos, photo }));
  }

  private async deleteOrphanedPhotos(previousSurvey: Survey, currentSurvey: Survey): Promise<void> {
    const currentPhotoFolderPaths = new Set(
      this.getAllPhotoContexts(currentSurvey)
        .map((c) => c.photo.imageFolderPath)
        .filter((p): p is string => !!p),
    );

    logger.debug('Current survey photo paths evaluated', { category: 'survey', count: currentPhotoFolderPaths.size });
    const orphanContexts = this.getAllPhotoContexts(previousSurvey).filter(({ photo }) => photo.imageFolderPath && !currentPhotoFolderPaths.has(photo.imageFolderPath));

    const limit = pLimit(DELETE_PHOTO_CONCURRENCY);
    const results = await Promise.allSettled(
      orphanContexts.map(({ measurementIndex, rowIndex, photo }) =>
        limit(() => {
          logger.debug('Deleting orphaned location photo', { category: 'survey', filename: photo.filename, measurementIndex, rowIndex });
          return this.deleteLocationPhoto(previousSurvey.id, measurementIndex, rowIndex, photo);
        }),
      ),
    );

    const deleted = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.filter((r) => r.status === 'rejected').length;

    results.forEach((r, i) => {
      if (r.status === 'rejected') {
        const { photo, measurementIndex, rowIndex } = orphanContexts[i];
        logger.error('Failed to delete orphaned photo', { category: 'survey', filename: photo.filename, measurementIndex, rowIndex, error: r.reason });
      }
    });

    logger.info('Photo reconciliation complete', { category: 'survey', deleted, failed });
  }

  private async uploadAllLocationPhotos(survey: Survey): Promise<void> {
    const contexts = this.getAllPhotoContexts(survey);
    const limit = pLimit(UPLOAD_PHOTO_CONCURRENCY);
    const results = await Promise.all(contexts.map((ctx) => limit(() => this.processSinglePhotoUpload(survey.id, ctx))));
    await this.finalizeUploads(survey, results, 'Photo');
  }

  private async uploadAllInstallerPhotos(survey: Survey): Promise<void> {
    const contexts = this.getInstallerPhotoContexts(survey);
    const limit = pLimit(UPLOAD_PHOTO_CONCURRENCY);
    const results = await Promise.all(contexts.map((ctx) => limit(() => this.processSingleInstallerPhotoUpload(survey.id, ctx))));
    await this.finalizeUploads(survey, results, 'Installer photo');
  }

  private async finalizeUploads(survey: Survey, results: PhotoUploadOutcome[], label: string): Promise<void> {
    const uploaded = results.filter((r) => r === 'uploaded').length;
    const failed = results.filter((r) => r === 'failed').length;
    logger.info(`${label} upload complete`, { category: 'survey', uploaded, failed });

    if (results.some((r) => r !== 'skipped')) {
      await this.idbSurveyService.updateSurvey(survey);
    }
  }

  private async processSinglePhotoUpload(surveyId: string, ctx: PhotoContext): Promise<PhotoUploadOutcome> {
    const { outcome, photo } = await this.processPhotoUpload(ctx.photo, (filename, bytes, mediaId) =>
      this.uploadLocationPhoto(surveyId, ctx.measurementIndex, ctx.rowIndex, filename, bytes, mediaId),
    );
    if (outcome !== 'skipped') {
      this.setPhotoAtIndex(ctx.location, ctx.photoIndex, photo);
    }
    return outcome;
  }

  private async processSingleInstallerPhotoUpload(surveyId: string, ctx: InstallerPhotoContext): Promise<PhotoUploadOutcome> {
    const { outcome, photo } = await this.processPhotoUpload(ctx.photo, (filename, bytes, mediaId) => this.uploadInstallerPhoto(surveyId, filename, bytes, mediaId));
    if (outcome !== 'skipped') {
      ctx.photos[ctx.photoIndex] = photo;
    }
    return outcome;
  }

  private async processPhotoUpload(
    photo: SurveyPhoto,
    upload: (filename: string, bytes: string, mediaId: string) => Promise<SurveyPhoto>,
  ): Promise<{ outcome: PhotoUploadOutcome; photo: SurveyPhoto }> {
    const hasAnnotations = !!photo.annotations?.length;

    if (photo.original && photo.web && photo.thumb && !photo.annotationsDirty) {
      if (photo.uploadStatus !== 'online') {
        return { outcome: 'status-updated', photo: { ...photo, uploadStatus: 'online' } };
      }
      return { outcome: 'skipped', photo };
    }

    try {
      const base64Data = await this.idbSurveyService.getPhotoContent(photo.filepath);

      if (!base64Data || base64Data.length === 0) {
        logger.error('Empty image data from IDB', { category: 'survey', filepath: photo.filepath });
        return { outcome: 'failed', photo: { ...photo, uploadStatus: 'failed' } };
      }

      let imageToUpload = base64Data;
      if (hasAnnotations) {
        if (this.flattenAnnotations) {
          imageToUpload = await this.flattenAnnotations(base64Data, photo.annotations);
        } else {
          logger.error('Photo has annotations but no flattener provided, uploading without burned-in annotations', { category: 'survey' });
        }
      }

      const onlinePhoto = await upload(photo.filename, imageToUpload, photo.filepath);

      return {
        outcome: 'uploaded',
        photo: {
          ...photo,
          imageFolderPath: onlinePhoto.imageFolderPath,
          original: onlinePhoto.original,
          web: onlinePhoto.web,
          thumb: onlinePhoto.thumb,
          uploadStatus: 'online',
          annotationsDirty: hasAnnotations && !this.flattenAnnotations,
        },
      };
    } catch (error) {
      logger.error('Photo upload error', { category: 'survey', error });
      return { outcome: 'failed', photo: { ...photo, uploadStatus: 'failed' } };
    }
  }

  private setPhotoAtIndex(location: Partial<LocationEntry>, photoIndex: number, updatedPhoto: SurveyPhoto): void {
    if (!location.photos || photoIndex >= location.photos.length) {
      logger.error('Invalid photo index or missing photos array', { category: 'survey', photoIndex, photosLength: location.photos?.length });
      return;
    }
    location.photos[photoIndex] = updatedPhoto;
  }

  private async deleteOrphanedInstallerPhotos(previousSurvey: Survey, currentSurvey: Survey): Promise<void> {
    const currentFolderPaths = new Set(
      this.getInstallerPhotoContexts(currentSurvey)
        .map((c) => c.photo.imageFolderPath)
        .filter((p): p is string => !!p),
    );

    const orphans = this.getInstallerPhotoContexts(previousSurvey).filter(({ photo }) => photo.imageFolderPath && !currentFolderPaths.has(photo.imageFolderPath));
    if (orphans.length === 0) {
      return;
    }

    const limit = pLimit(DELETE_PHOTO_CONCURRENCY);
    await Promise.allSettled(
      orphans.map(({ photo }) =>
        limit(() => {
          logger.debug('Deleting orphaned installer photo', { category: 'survey', filename: photo.filename });
          return this.deleteInstallerPhoto(previousSurvey.id, photo);
        }),
      ),
    );
  }

  private stripUndefined<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj)) as T;
  }
}
